import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useMemo,
  ChangeEvent
} from "react";
import { GetWSMgr } from "@/Utils/StateLib";
import { DATA_SOURCES_TEST_PREFIX } from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/DataSourceConfiguration/Utils";
import { QManageTablesRFC } from "@coreapp/CoreApp/QManageTablesRFC";
import { Globals } from "@coreapp/CoreApp/Globals";
import { appstr } from "@coreapp/utils/appstr";
import { kReservedFields, enumObjectTypes } from "@coreapp/constants";
import {
  Flex,
  Button,
  Menu,
  Alert,
  Input,
  Table,
  Icon,
  Divider,
  BigInfoIcon,
  Text
} from "@theorgchartdev/storybook";
import { useTableManagerContext } from "@/providers/AppendTableProvider";
import cx from "classnames";
import { UpdateChartMetadata } from "@/Utils/Promises";
import { useToast } from "@/providers/ToastProvider";
import { useChartSettingsContext } from "@/providers/ChartSettingsProvider";
import { GetNeedRefreshDiagnostics } from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/Utils";
import { IsPositionChart } from "@coreapp/utils/IsPositionChart";
import { TypeCell } from "./ModificationsTab";
import { QTableManager } from "@coreapp/CoreApp/QTableManager";
import ActionListItem from "@/appComponents/Blocks/Composites/ActionListItem/ActionListItem";

type ManageFieldsTabProps = {
  tableMgr: { state: QTableManager };
  onClose: () => void;
  onFieldChange: () => void;
  onValidityChange?: (isValid: boolean) => void;
};

type FieldData = {
  field: string;
  isReserved?: boolean;
  isBlank: boolean;
  isInUse?: boolean;
  disabled: boolean;
  type?: (typeof enumObjectTypes)[keyof typeof enumObjectTypes];
};

function areSetsEqual(setA: Set<any>, setB: Set<any>): boolean {
  if (setA.size !== setB.size) {
    return false;
  }

  for (const item of setA) {
    if (!setB.has(item)) {
      return false;
    }
  }

  return true;
}

export const ManageFieldsTab = forwardRef((props: ManageFieldsTabProps, ref) => {
  const [_, setIsSaving] = useState(false);
  const [fieldsData, setFields] = useState<FieldData[]>([]);
  const [validation, setValidation] = useState({ isValid: true, status: "default", message: "" });
  const [isDirty, setIsDirty] = useState(false);
  const initialFieldsRef: React.MutableRefObject<string | null> = useRef(null);
  const { addToast } = useToast() as any;
  const { chartConfigRef, setChartConfig } = useChartSettingsContext();

  const tableMgr = props?.tableMgr?.state;

  const isPositionChart = IsPositionChart(chartConfigRef.current);

  const dataTestIdPrefix = "append-table-field-editor-";
  const newDataTestIdPrefix = `${DATA_SOURCES_TEST_PREFIX}-append-table-field-editor-`;
  const noFields = fieldsData.length === 0;

  useEffect(() => {
    if (!initialFieldsRef.current) {
      const posFields = fieldsData
        .filter((item: FieldData) => item.type === enumObjectTypes.position)
        .map(field => field.field);
      const holderFields = fieldsData
        .filter((item: FieldData) => item.type === enumObjectTypes.holder)
        .map(field => field.field);
      initialFieldsRef.current = JSON.stringify({
        [enumObjectTypes.position]: posFields,
        [enumObjectTypes.holder]: holderFields
      });
    }
  }, []);

  // Track changes to determine dirty state
  useEffect(() => {
    if (initialFieldsRef.current) {
      let initialFields = JSON.parse(initialFieldsRef.current);

      const sameHolderFields = new Set(
        fieldsData.filter(field => field.type === enumObjectTypes.holder).map(field => field.field)
      );
      const samePositionFields = new Set(
        fieldsData
          .filter(field => field.type === enumObjectTypes.position)
          .map(field => field.field)
      );

      const holderFieldsChanged = !areSetsEqual(
        new Set(initialFields[enumObjectTypes.holder]),
        sameHolderFields
      );
      const positionFieldsChanged = !areSetsEqual(
        new Set(initialFields[enumObjectTypes.position]),
        samePositionFields
      );

      // Compare arrays of field values
      const hasChanges = holderFieldsChanged || positionFieldsChanged;
      setIsDirty(hasChanges);
    }
  }, [fieldsData]);

  const {
    tableEditorContextValue: { showTableEditor }
  } = useTableManagerContext();

  useEffect(() => {
    if (props.onValidityChange) {
      props.onValidityChange(validation.isValid);
    }
  }, [validation.isValid, props.onValidityChange]);

  useImperativeHandle(ref, () => ({
    validate: async () => {
      return handleFieldValidation();
    },
    save: async () => {
      try {
        setIsSaving(true);
        const { removedFields } = (await sendUpdate()) as any;
        if (removedFields && removedFields.length > 0) {
          await updateChartConfig(removedFields);
        }
        setIsSaving(false);
        setIsDirty(false);
        syncFields();
        return true;
      } catch (error) {
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    checkValidity: async () => {
      return validation.isValid;
    },
    hasUnsavedChanges: async () => {
      return isDirty;
    },
    modCount: () => {
      return fieldsData?.length ?? 0;
    }
  }));

  const canRemoveFieldReference = (fieldID: string): boolean => {
    return fieldID === "AuxSuperID" || /^AuxSuperID-\d+$/.test(fieldID);
  };

  const isAdditionalReportField = (fieldName: string): boolean => {
    return fieldName === "Additional Report" || /^Additional Report \d+$/.test(fieldName);
  };

  const InputCellRenderer = ({ rowState }: any) => {
    const [inputValue, setInputValue] = useState(rowState.data.fieldData.field);
    if (!rowState) return null;
    return (
      <div style={{ width: "100%" }}>
        <Input
          data-testid={`append-table-addt-field-${rowState.data.index}`}
          placeholder={appstr("Enter a field name")}
          size="medium"
          status={getInputStatus(rowState.data.fieldData)}
          message={getInputMessage(rowState.data.fieldData)}
          value={inputValue}
          onChange={(e: any) => setInputValue(e?.target?.value)}
          onBlur={handleInputFieldChange(rowState.data.index)}
          disabled={rowState.data.fieldData.disabled}
        />
      </div>
    );
  };

  const updateChartConfig = async (deletedFields: string[]) => {
    const chartConfig = { ...chartConfigRef.current };
    const newMapping = { ...chartConfig.fields };
    const newHeaders = [...chartConfig.headers];

    deletedFields.forEach(fieldName => {
      Object.keys(newMapping).forEach(fieldID => {
        if (newMapping[fieldID] === fieldName) {
          if (canRemoveFieldReference(fieldID)) {
            delete newMapping[fieldID];
            const headerIndex = newHeaders.indexOf(fieldName);
            if (headerIndex !== -1) {
              newHeaders.splice(headerIndex, 1);
            }
          }
        }
      });
    });

    const nextSync = GetNeedRefreshDiagnostics(chartConfigRef.current);
    const configChanges = { fields: newMapping, headers: newHeaders, ...nextSync };
    const { success } = await UpdateChartMetadata(chartConfig.ID, JSON.stringify(configChanges));

    if (success) {
      addToast({ message: appstr("Chart fields changed (long)"), status: "warning" });
      const nextChartConfig = { ...chartConfig, ...configChanges };
      setChartConfig(nextChartConfig);
      chartConfigRef.current = nextChartConfig;
    } else {
      addToast({ message: appstr("An error occurred while saving"), status: "error" });
    }
  };

  const syncFields = () => {
    const appendTable = structuredClone(tableMgr?.fTableInfo);
    const additionalFields = appendTable["additionalFields"] || (isPositionChart ? [] : [""]);
    const additionalPosFields = appendTable["additionalPositionFields"] || [];

    initialFieldsRef.current = JSON.stringify({
      [enumObjectTypes.position]: additionalPosFields,
      [enumObjectTypes.holder]: additionalFields
    });

    const nextFieldsData = additionalFields.map((field: string) => ({
      field,
      type: enumObjectTypes.holder,
      isReserved: isInternalField(field),
      isBlank: field === "",
      isInUse: additionalFields.filter((item: string) => item === field && item !== "").length > 1,
      disabled: field !== ""
    }));
    const posFieldsData = additionalPosFields.map((field: string) => ({
      field,
      type: enumObjectTypes.position,
      isReserved: isInternalField(field),
      isBlank: field === "",
      isInUse:
        additionalPosFields.filter((item: string) => item === field && item !== "").length > 1,
      disabled: field !== ""
    }));
    nextFieldsData.push(...posFieldsData);

    setFields(nextFieldsData);
  };

  useEffect(() => {
    syncFields();
    return () => {
      handleClose();
    };
  }, [showTableEditor, props.tableMgr]);

  useEffect(() => {
    handleFieldValidation();
  }, [fieldsData]);

  const handleClose = () => {
    setFields([]);
    setIsSaving(false);
    setValidation({ status: "default", message: "", isValid: true });
    if (props.onClose) props.onClose();
  };

  const sendUpdate = async () => {
    return new Promise((resolve, reject) => {
      const appendTable = structuredClone(tableMgr.fTableInfo);

      const currentFields = fieldsData.reduce((fields: string[], data: FieldData) => {
        if (data.type !== enumObjectTypes.position && data.field !== "") {
          return [...fields, data.field];
        }
        return fields;
      }, []);

      const currentPositionFields = fieldsData.reduce((fields: string[], data: FieldData) => {
        if (data.type === enumObjectTypes.position && data.field !== "") {
          return [...fields, data.field];
        }
        return fields;
      }, []);

      const initialFields = initialFieldsRef.current
        ? JSON.parse(initialFieldsRef.current)
        : { position: [], holder: [] };

      const removedFields = initialFields[enumObjectTypes.holder].filter(
        (field: string) => !currentFields.includes(field)
      );
      const removedPositionFields = initialFields[enumObjectTypes.position].filter(
        (field: string) => !currentPositionFields.includes(field)
      );

      appendTable["additionalFields"] = currentFields;
      appendTable["additionalPositionFields"] = currentPositionFields;

      if (removedFields.length > 0) appendTable["deletedFields"] = removedFields;
      // TODO: Once we can edit new position fields, we need to handle their deletions logic.
      if (removedPositionFields.length > 0)
        appendTable["positionDeletedFields"] = removedPositionFields;

      const wsMgr = GetWSMgr();
      const data = JSON.stringify(appendTable);
      const additionalFieldsRFC = new QManageTablesRFC();

      const callbackObj = {
        fWSMgr: wsMgr,
        fTableMgr: tableMgr,
        resolve: () => {
          resolve({ removedFields });
        },
        reject
      };

      additionalFieldsRFC.InitUpdateTables(callbackObj);
      const accountName = wsMgr?.GetAccountName();
      const username = wsMgr?.GetUserName();
      additionalFieldsRFC.SetParams(accountName, username, "saveAdditionalFields", data);
      Globals._rfcMgr.QueueRFC(additionalFieldsRFC);
    });
  };

  const isInternalField = (field: string): boolean => {
    const lowerCaseField = field.toLowerCase();
    return kReservedFields.some(reservedField => reservedField.toLowerCase() === lowerCaseField);
  };

  const handleInputFieldChange = (idx: number) => (event: ChangeEvent<HTMLInputElement>) => {
    const fieldName = event.target.value;

    setFields(prev => {
      const updatedFields = prev.map((data, i) =>
        i === idx
          ? {
              ...data,
              field: fieldName,
              isReserved: isInternalField(fieldName) || isAdditionalReportField(fieldName)
            }
          : data
      );

      return updatedFields.map(data => ({
        ...data,
        isInUse:
          updatedFields.filter(item => item.field === data.field && item.field !== "").length > 1,
        isBlank: data.field === "" // Flag if the field is blank
      }));
    });
  };

  const handleFieldValidation = () => {
    const invalidField = fieldsData.find(f => f.isReserved);
    if (invalidField) {
      setValidation({
        isValid: false,
        message: appstr("Additional field is reserved (long)"),
        status: "error"
      });
      return false;
    }

    const isDuplicateField = fieldsData.find(f => f.isInUse);
    if (isDuplicateField) {
      setValidation({
        isValid: false,
        message: appstr("Additional field in use (long)"),
        status: "error"
      });
      return false;
    }

    setValidation({ isValid: true, status: "default", message: "" });
    return true;
  };

  const handleRemove = (fieldIdx: number, skipValidation = false) => {
    const fieldData = fieldsData[fieldIdx];
    const field = fieldData.field;
    const isExistingField = fieldData.disabled;
    if (!skipValidation && isExistingField) {
      const wsMgr = GetWSMgr();
      // should this alert always be thrown?
      wsMgr?.CreateReactAlert(
        appstr("Delete additional field"),
        appstr("Delete additional field (long)", { name: field }),
        () => handleRemove(fieldIdx, true),
        () => {},
        appstr("Delete"),
        appstr("Cancel"),
        null,
        { alertProps: { message: appstr("Broken field references warning"), severity: "warning" } }
      );
      return;
    }
    setFields(prev => prev.filter((_, idx) => idx !== fieldIdx));
    props.onFieldChange();
  };

  const getInputMessage = (data: FieldData) => {
    if (data.isReserved) {
      return appstr("Name is reserved");
    } else if (data.isInUse) {
      return appstr("Name already in use");
    }
    return "";
  };

  const getInputStatus = (data: FieldData) => {
    if (data.isReserved || data.isInUse) {
      return "error";
    }
    return "default";
  };

  const handleAddEmployeeField = () => {
    setFields((prev: FieldData[]) => [
      ...prev,
      {
        field: "",
        type: enumObjectTypes.holder,
        isReserved: false,
        isBlank: false,
        disabled: false
      }
    ]);
    props.onFieldChange();
  };

  const handleAddPositionField = () => {
    setFields(prev => [
      ...prev,
      {
        field: "",
        type: enumObjectTypes.position,
        isReserved: false,
        isBlank: false,
        disabled: false
      }
    ]);
    props.onFieldChange();
  };

  const columns = [
    {
      field: "appliesTo",
      headerName: appstr("Applies to"),
      type: "cellRenderer",
      columnWidth: "twenty",
      CellRenderer: TypeCell
    },
    {
      field: "fieldName",
      headerName: appstr("Field name"),
      type: "cellRenderer",
      columnWidth: "fifty",
      CellRenderer: InputCellRenderer
    },
    {
      field: "fieldType",
      headerName: appstr("fieldtype"),
      type: "text",
      columnWidth: "twenty"
    },
    {
      field: "actions",
      headerName: "",
      type: "cellRenderer",
      columnWidth: "ten",
      CellRenderer: ({ rowState }: any) => {
        return (
          <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end", flexShrink: 0 }}>
            <Button
              data-testid={`${newDataTestIdPrefix}delete-field-${rowState.data.index}`}
              size={"small"}
              modifier={"tertiary"}
              color="error"
              iconProps={{ name: "trash-can" }}
              iconOnly
              onClick={() => handleRemove(rowState.data.index)}
            />
          </div>
        );
      },
      filterDisabled: true,
      sortDisabled: true
    }
  ];

  const BuildTableData = (fields: FieldData[]) => {
    return fields.map((field: FieldData, index: number) => {
      const type = field.type;
      const isPosition = type === enumObjectTypes.position;
      const fieldTypeLabel = isPosition ? appstr("Position") : appstr("Employee");

      return {
        appliesTo: fieldTypeLabel,
        fieldName: field.field,
        type,
        fieldType: appstr("Text"),
        isPosition,
        fieldData: field,
        index
      };
    });
  };

  const tableData = useMemo(() => BuildTableData(fieldsData), [fieldsData]);

  return (
    <Flex
      direction={"column"}
      gap={"16px"}
      overflow={"hidden"}
      className={cx({
        [`${dataTestIdPrefix}tab-container-full-height`]: noFields
      })}
    >
      {!isPositionChart && (
        <Text modifier={"default"} size={"small"}>
          {appstr("About additional fields (long)")}
        </Text>
      )}
      <span>
        {isPositionChart ? (
          <Menu
            dataTestId={`${dataTestIdPrefix}create-field`}
            trigger={
              <Button
                style={{ flexShrink: "0" }}
                modifier={"secondary"}
                className="oc-append-table-add-type-button"
                iconProps={{ name: "plus" }}
              >
                {appstr("Add field")}{" "}
                <Icon name="chevron-down" size="xsmall" style={{ width: 9, marginLeft: 8 }} />
              </Button>
            }
            listWidth={"fill"}
            value={""}
            options={[
              {
                label: appstr("Employee"),
                icon: "image-user",
                value: enumObjectTypes.holder,
                clickHandler: handleAddEmployeeField,
                dataTestId: `${dataTestIdPrefix}add-employee`
              },
              {
                label: appstr("Position"),
                icon: "cubes",
                clickHandler: handleAddPositionField,
                value: enumObjectTypes.position,
                dataTestId: `${dataTestIdPrefix}add-position`
              }
            ]}
          />
        ) : (
          <Button
            dataTestId={"append-table-add-custom-field"}
            modifier={"secondary"}
            onClick={handleAddEmployeeField}
            iconProps={{
              name: "plus"
            }}
          >
            {appstr("Add field")}
          </Button>
        )}
      </span>
      {validation.status === "error" && (
        <Flex width={"100%"}>
          <Alert
            severity={validation.status}
            showIcon
            className={cx("oc-padding-small", "oc-text-small", "oc-data-source-details")}
          >
            {validation.message}
          </Alert>
        </Flex>
      )}
      <Flex
        className={cx(
          "oc-list-container",
          "ocn-list-scrollbar",
          `${dataTestIdPrefix}table-container`
        )}
        style={{ flex: 1, minHeight: 0 }}
        direction={"column"}
        gap={"16px"}
      >
        {isPositionChart ? (
          <div style={{ flex: 1, overflow: "auto" }} className={"ocn-list-scrollbar ocn-flex"}>
            {noFields ? (
              <BigInfoIcon message={appstr("No fields added yet")} name="table" color="brand" />
            ) : (
              <Table
                tableLayout={"fixed"}
                columns={columns}
                data={tableData}
                showHeader
                sortEnabled
                filterEnabled
                containerPadding
                rowSize={"largeR"}
                textSize={"mediumT"}
              />
            )}
          </div>
        ) : (
          fieldsData.map((data, idx) => (
            <Flex direction={"column"} gap={"16px"} key={idx}>
              <ActionListItem
                visible="always"
                innerFlexProps={{
                  overflow: "visible"
                }}
                contentBefore={
                  <div style={{ width: "100%" }}>
                    <Input
                      data-testid={`append-table-addt-field-${idx}`}
                      placeholder={appstr("Enter a field name")}
                      size="medium"
                      status={getInputStatus(data)}
                      message={getInputMessage(data)}
                      value={data.field}
                      onChange={handleInputFieldChange(idx) as any}
                      disabled={data.disabled}
                    />
                  </div>
                }
                actions={[
                  {
                    dataTestId: `${newDataTestIdPrefix}delete-field-${idx}`,
                    iconName: "trash-can",
                    iconProps: { color: "error" },
                    onClick: () => handleRemove(idx)
                  }
                ]}
              />
              <Divider vertical={false} />
            </Flex>
          ))
        )}
      </Flex>
    </Flex>
  );
});
